import React from "react";
import { Plug, Search, GitBranch } from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const MODES = [
  {
    icon: Plug,
    title: "PunchOut Catalog",
    body:
      "OCI / cXML PunchOut into SAP Ariba, Coupa, Oracle iProcurement, Jaggaer and ServiceNow. Buyers shop in OMNISupply and return cart line-items to the host ERP for approval.",
    tag: "Most common",
  },
  {
    icon: Search,
    title: "Embedded Search API",
    body:
      "REST API endpoints exposing catalog search, price-checks and stock availability. Embed the OMNISupply storefront natively inside your buyer portal.",
    tag: "Developer-led",
  },
  {
    icon: GitBranch,
    title: "Deep ERP Integration",
    body:
      "Full bi-directional sync: PO creation, ASN, GRN, invoice flip and master data. Available for SAP S/4HANA, Oracle Fusion, Microsoft Dynamics and Workday.",
    tag: "Strategic",
  },
];

const ERPS = ["SAP Ariba", "Coupa", "Oracle iProcurement", "Jaggaer", "ServiceNow", "SAP S/4HANA", "Oracle Fusion", "Microsoft Dynamics", "Workday"];

export default function Integrations() {
  const { t } = useLocale();
  return (
    <section
      id="integrations"
      data-testid="integrations-section"
      className="bg-slate-50 py-24 md:py-32 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-4">
            <span className="inline-block h-px w-8 bg-slate-300" />
            <span>{t("landing.integrations.eyebrow")}</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[56px] font-semibold tracking-[-0.02em] mt-1 text-slate-900 leading-[1.05]">
            {t("landing.integrations.headline")}
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed font-light">
            {t("landing.integrations.body")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {MODES.map((m, i) => (
            <div
              key={m.title}
              data-testid={`integration-mode-${i}`}
              className="rounded-xl bg-white border border-slate-200 p-7 hover:border-[#0072CE] hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="h-11 w-11 rounded-lg bg-[#0072CE]/10 border border-[#0072CE]/15 text-[#0072CE] flex items-center justify-center">
                  <m.icon size={20} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded">
                  {m.tag}
                </span>
              </div>
              <h3 className="font-display text-xl font-semibold tracking-tight text-slate-900">
                {m.title}
              </h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                {m.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-xl bg-white border border-slate-200 p-6 md:p-8">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-4">
            {t("landing.integrations.tested")}
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {ERPS.map((e) => (
              <span
                key={e}
                className="font-display text-slate-700 hover:text-[#0072CE] transition-colors text-base md:text-lg font-semibold tracking-tight"
                data-testid={`integration-erp-${e.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {e}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
